import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@patternfly/react-icons';
import { AdminUserDetail } from '@app/Users/types';
import { getUser, grantAiCredits } from '@app/Users/users.api';
import { RoleChips } from '@app/Users/RoleChips';
import { Pill } from '@app/Campaigns/badges';
import { colors, radii, shadows } from '@app/components/designTokens';

const subTone = (status: string | null): 'green' | 'amber' | 'red' | 'gray' => {
  switch (status) {
    case 'ACTIVE':
      return 'green';
    case 'TRIALING':
      return 'amber';
    case 'PAST_DUE':
    case 'INCOMPLETE':
      return 'red';
    default:
      return 'gray';
  }
};

export const UserDetailPage: React.FunctionComponent = () => {
  const { userId } = useParams();
  const id = Number(userId);
  const navigate = useNavigate();

  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [grantAmount, setGrantAmount] = useState('5');
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);
  const [grantMsg, setGrantMsg] = useState<string | null>(null);
  const [grantErr, setGrantErr] = useState<string | null>(null);

  const onGrant = async () => {
    const amount = Number(grantAmount);
    if (!Number.isInteger(amount) || amount < 1 || amount > 100) {
      setGrantErr('Enter a whole number between 1 and 100');
      setGrantMsg(null);
      return;
    }
    setGranting(true);
    setGrantErr(null);
    setGrantMsg(null);
    try {
      const r = await grantAiCredits(id, amount, grantNote.trim() || undefined);
      setGrantMsg(`Granted ${r.granted} AI ad credit${r.granted === 1 ? '' : 's'} for this period.`);
      setGrantNote('');
      setDetail(await getUser(id));
    } catch (e) {
      setGrantErr(e instanceof Error ? e.message : 'Failed to grant AI credits');
    } finally {
      setGranting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUser(id)
      .then(d => {
        if (!cancelled) setDetail(d);
      })
      .catch(e => {
        if (!cancelled) setError(e?.message || 'Failed to load user');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: colors.accent, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 16 },
    headerRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 6 },
    title: { fontSize: 24, fontWeight: 800, color: colors.title, margin: 0 },
    sub: { fontSize: 14, color: colors.subtle, marginBottom: 20 },
    statRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 },
    stat: { flex: '1 1 150px', background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, padding: '14px 16px' },
    statLabel: { fontSize: 12, fontWeight: 600, color: colors.subtle, textTransform: 'uppercase', letterSpacing: '0.3px' },
    statValue: { fontSize: 24, fontWeight: 800, color: colors.title, marginTop: 4 },
    panels: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 },
    panel: { background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, boxShadow: shadows.softCard, padding: 18 },
    panelTitle: { fontSize: 15, fontWeight: 700, color: colors.title, margin: '0 0 14px' },
    row: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${colors.bgMuted}`, fontSize: 14 },
    rowLabel: { color: colors.subtle },
    grantWrap: { marginTop: 14, paddingTop: 14, borderTop: `1px solid ${colors.bgMuted}` },
    grantRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    numInput: { width: 80, padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, outline: 'none' },
    noteInput: { flex: '1 1 140px', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, outline: 'none' },
    grantBtn: { padding: '8px 14px', border: 'none', borderRadius: radii.button, background: colors.accent, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }
  };

  const stat = (label: string, value: React.ReactNode) => (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );
  const row = (label: string, value: React.ReactNode) => (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      <span style={{ fontWeight: 600, color: colors.body }}>{value}</span>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.wrap}>
        <button style={styles.back} onClick={() => navigate('/users')}>
          <ArrowLeftIcon /> Back to Users
        </button>
        <div style={{ ...styles.panel, padding: 48, textAlign: 'center', color: colors.subtle }}>Loading user…</div>
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div style={styles.wrap}>
        <button style={styles.back} onClick={() => navigate('/users')}>
          <ArrowLeftIcon /> Back to Users
        </button>
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 12, padding: 20 }}>{error || 'Not found'}</div>
      </div>
    );
  }

  const u = detail.summary;
  const orgs = detail.organizations;

  return (
    <div style={styles.wrap}>
      <button style={styles.back} onClick={() => navigate('/users')}>
        <ArrowLeftIcon /> Back to Users
      </button>

      <div style={styles.headerRow}>
        <h1 style={styles.title}>{u.displayName || `User #${u.userId}`}</h1>
        {u.emailVerified ? <Pill text="VERIFIED" tone="green" /> : <Pill text="UNVERIFIED" tone="amber" />}
      </div>
      <div style={styles.sub}>
        {u.email} · {u.authProvider || 'local'} · id {u.userId}
      </div>

      <div style={{ marginBottom: 22 }}>
        <RoleChips roles={u.roles} />
      </div>

      <div style={styles.statRow}>
        {stat('Ads created', <>{u.adsCreated}<span style={{ fontSize: 13, color: colors.subtle, fontWeight: 500 }}> · {u.aiAdsCreated} AI</span></>)}
        {stat('Campaigns', u.campaignsCreated)}
        {stat('Runs booked', u.runsBooked)}
      </div>

      <div style={styles.panels}>
        <div style={styles.panel}>
          <h3 style={styles.panelTitle}>Subscription</h3>
          {u.subscriptionTier ? (
            <>
              {row('Tier', <strong>{u.subscriptionTier}</strong>)}
              {row('Status', u.subscriptionStatus ? <Pill text={u.subscriptionStatus} tone={subTone(u.subscriptionStatus)} /> : '—')}
              {row('Credits used (period)', u.creditsUsedThisPeriod ?? '—')}
              {row('AI ads (period)', u.aiAdsThisPeriod ?? '—')}
              {u.subscriptionTier === 'BRONZE' ? (
                <div style={{ ...styles.grantWrap, fontSize: 13, color: colors.subtle }}>
                  Bronze does not include AI ads — credit grants have no effect. Upgrade the tier instead.
                </div>
              ) : (
              <div style={styles.grantWrap}>
                <div style={{ ...styles.statLabel, marginBottom: 8 }}>Grant AI ad credits</div>
                <div style={styles.grantRow}>
                  <input
                    style={styles.numInput}
                    type="number"
                    min={1}
                    max={100}
                    value={grantAmount}
                    onChange={e => setGrantAmount(e.target.value)}
                    disabled={granting}
                  />
                  <input
                    style={styles.noteInput}
                    placeholder="Note (optional, audit log)"
                    value={grantNote}
                    onChange={e => setGrantNote(e.target.value)}
                    disabled={granting}
                  />
                  <button
                    style={{ ...styles.grantBtn, opacity: granting ? 0.6 : 1 }}
                    onClick={onGrant}
                    disabled={granting}
                  >
                    {granting ? 'Granting…' : 'Grant'}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: colors.subtle, marginTop: 8 }}>
                  Lowers “AI ads (period)” usage so the user can generate more this period. Resets at renewal.
                </div>
                {grantErr && <div style={{ fontSize: 13, color: colors.danger, marginTop: 8 }}>{grantErr}</div>}
                {grantMsg && <div style={{ fontSize: 13, color: colors.success, marginTop: 8 }}>{grantMsg}</div>}
              </div>
              )}
            </>
          ) : (
            <div style={{ color: colors.placeholder, fontSize: 14 }}>No subscription.</div>
          )}
        </div>

        <div style={styles.panel}>
          <h3 style={styles.panelTitle}>Organizations ({orgs.length})</h3>
          {orgs.length === 0 ? (
            <div style={{ color: colors.placeholder, fontSize: 14 }}>No organizations.</div>
          ) : (
            orgs.map(o => (
              <div key={o.id} style={styles.row}>
                <span style={{ fontWeight: 600 }}>{o.name || `Org #${o.id}`}</span>
                <span style={{ color: colors.subtle }}>{o.category || '—'}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
